import { useAuth } from '@agentnet/auth';
import { pxToRem } from '@fluentsms/agentnet-web-components';
import { Box, Grid, InputAdornment, Tooltip, Typography } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { updateFincenReportingPartyAPI } from 'api/fincen-report-api';
import { isValid, parse } from 'date-fns';
import type { FincenUpdateResponse, ReportingPartyFormData } from 'features/files/fincen-reporting/types';
import { FileDataContext, FileDataContextInterface } from 'hooks/FileDataContext';
import { FincenReportContext, FincenReportContextInterface } from 'hooks/FincenReportContext';
import { ProfileContext, ProfileContextInterface } from 'hooks/ProfileContext';
import useAsync from 'hooks/useAsync';
import React, { FC, useContext, useEffect, useState } from 'react';
import useSnackBars from 'ui-kit/components/notification/useSnackbars';
import RadioGroup from 'ui-kit/components/radios/RadioGroup';
import MagicWandIcon from 'ui-kit/icons/MagicWand';
import { AgentNetDropdownSelector } from 'ui-kit/inputs';
import DateFieldString from 'ui-kit/inputs/DateField/DateFieldString';
import territories from 'ui-kit/inputs/geo-data/us-territory.json';
import canadaProvinces from 'ui-kit/inputs/geo-data/canada-provinces.json';
import mexicoStates from 'ui-kit/inputs/geo-data/mexico-states.json';
import foreignTerritories from 'ui-kit/inputs/geo-data/foreign-territory.json';
import GoogleAutocompleteSearch from 'ui-kit/inputs/GoogleAutocompleteSearch/GoogleAutocompleteSearch';
import statesData from 'ui-kit/inputs/StateSelectField/states.json';
import { AgentNetTextInput } from 'ui-kit/inputs/TextField';
import { CountryCode, requiresStateAndPostalCode, isUSTerritory, isUS } from '@agentnet/utils';
import USPostalCodeField from 'ui-kit/inputs/USPostalCodeField/USPostalCodeField';
import { FieldValidationError } from 'utilities/validation/validation';
import { AsyncStatus } from '../constants';
import { PARTY_CATEGORIES } from './constants';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      padding: theme.spacing(2),
    },
    formLabel: {
      fontWeight: 500,
      fontSize: pxToRem(14),
      marginBottom: theme.spacing(1),
    },
    radioGroupContainer: {
      marginTop: theme.spacing(1),
    },
  }),
);

interface ReportingPartyFormProps {
  onChange?: (field: string, value: string) => void;
  onValidationChange?: (isValid: boolean) => void;
  submitted?: boolean;
  onFormSubmissionComplete?: () => void;
}

const ReportingPartyForm: FC<ReportingPartyFormProps> = ({
  onChange,
  onValidationChange,
  submitted = false,
  onFormSubmissionComplete,
}) => {
  const classes = useStyles();
  const fincenReportCtx = useContext<FincenReportContextInterface | null>(FincenReportContext);
  const fileDataCtx = useContext<FileDataContextInterface | null>(FileDataContext);
  const profileCtx = useContext<ProfileContextInterface | null>(ProfileContext);
  const { profile } = profileCtx ?? {};

  const { fincenReport, updateReportingParty, reportingPartyValidationErrors, clearReportingPartyValidationErrors } =
    fincenReportCtx ?? {};

  const { fileData } = fileDataCtx ?? {};
  const { getAccessToken } = useAuth();
  const { addSnackbarMessage } = useSnackBars();
  const reportPartyData: Partial<ReportingPartyFormData> =
    (fincenReport?.reportingParty as ReportingPartyFormData) || {};

  const hasSavedReportingPartyData = (data: Partial<ReportingPartyFormData> | undefined): boolean => {
    if (!data) return false;
    return !!(data.firstName?.trim() || data.lastName?.trim() || data.entityName?.trim() || data.partyCategory?.trim());
  };

  const hasSavedData = hasSavedReportingPartyData(reportPartyData);

  const [formData, setFormData] = useState<ReportingPartyFormData>(() => {
    const autoFilledFields: string[] = [];

    const firstName = hasSavedData ? reportPartyData.firstName || '' : profile?.firstName || '';
    const middleName = hasSavedData ? reportPartyData.middleName || '' : profile?.middleName || '';
    const lastName = hasSavedData ? reportPartyData.lastName || '' : profile?.lastName || '';

    if (profile) {
      if (firstName && firstName === (profile.firstName || '')) {
        autoFilledFields.push('firstName');
      }
      if (middleName && middleName === (profile.middleName || '')) {
        autoFilledFields.push('middleName');
      }
      if (lastName && lastName === (profile.lastName || '')) {
        autoFilledFields.push('lastName');
      }
    }

    return {
      partyCategory: reportPartyData.partyCategory || '',
      partyType: reportPartyData.partyType || 'Individual',
      address1: reportPartyData.address1 || '',
      address2: reportPartyData.address2 || '',
      city: reportPartyData.city || '',
      usTerritory: reportPartyData.usTerritory || '',
      state: reportPartyData.state || '',
      postalCode: reportPartyData.postalCode || '',
      firstName,
      middleName,
      lastName,
      suffix: reportPartyData.suffix || '',
      entityName: reportPartyData.entityName || '',
      closingDate: reportPartyData.closingDate,
      autoFilledFields,
    };
  });

  const [validationErrors, setValidationErrors] = useState<FieldValidationError[]>([]);
  const [formSubmitted, setFormSubmitted] = useState<boolean>(submitted);

  const [originalProfileValues] = useState<{
    firstName?: string;
    middleName?: string;
    lastName?: string;
  }>(() => ({
    firstName: profile?.firstName || '',
    middleName: profile?.middleName || '',
    lastName: profile?.lastName || '',
  }));

  // Track if we've already loaded data from fincenReport to avoid overwriting user edits
  const [hasLoadedFromReport, setHasLoadedFromReport] = useState(false);

  // Update form data when fincenReport loads (e.g., on page refresh)
  useEffect(() => {
    // Only load from report if we haven't already and there's report data available
    if (!hasLoadedFromReport && fincenReport?.reportingParty) {
      const reportData = fincenReport.reportingParty as ReportingPartyFormData;
      const hasSavedReportData = !!(
        reportData.firstName?.trim() ||
        reportData.lastName?.trim() ||
        reportData.entityName?.trim() ||
        reportData.partyCategory?.trim() ||
        reportData.address1?.trim()
      );

      if (hasSavedReportData) {
        const autoFilledFields: string[] = [];

        const firstName = reportData.firstName || '';
        const middleName = reportData.middleName || '';
        const lastName = reportData.lastName || '';

        if (profile) {
          if (firstName && firstName === (profile.firstName || '')) {
            autoFilledFields.push('firstName');
          }
          if (middleName && middleName === (profile.middleName || '')) {
            autoFilledFields.push('middleName');
          }
          if (lastName && lastName === (profile.lastName || '')) {
            autoFilledFields.push('lastName');
          }
        }

        setFormData({
          partyCategory: reportData.partyCategory || '',
          partyType: reportData.partyType || 'Individual',
          address1: reportData.address1 || '',
          address2: reportData.address2 || '',
          city: reportData.city || '',
          usTerritory: reportData.usTerritory || '',
          state: reportData.state || '',
          postalCode: reportData.postalCode || '',
          firstName,
          middleName,
          lastName,
          suffix: reportData.suffix || '',
          entityName: reportData.entityName || '',
          closingDate: reportData.closingDate,
          autoFilledFields,
        });

        setHasLoadedFromReport(true);
      }
    }
  }, [fincenReport?.reportingParty, hasLoadedFromReport, profile]);

  const isFieldAutoFilled = (fieldName: string): boolean => {
    return formData.autoFilledFields?.includes(fieldName) || false;
  };

  const renderMagicWandIcon = (fieldName: string): JSX.Element | undefined => {
    if (isFieldAutoFilled(fieldName)) {
      return (
        <InputAdornment position="end">
          <Tooltip title="Auto-filled field">
            <MagicWandIcon style={{ fontSize: pxToRem(20), cursor: 'help' }} />
          </Tooltip>
        </InputAdornment>
      );
    }
    return undefined;
  };

  useEffect(() => {
    if (!hasSavedData && profile && formData.partyType === 'Individual') {
      const hasProfileData = profile.firstName || profile.middleName || profile.lastName;
      if (hasProfileData) {
        const autoFilledFields: string[] = [];
        if (profile.firstName) autoFilledFields.push('firstName');
        if (profile.middleName) autoFilledFields.push('middleName');
        if (profile.lastName) autoFilledFields.push('lastName');

        setFormData((prev) => ({
          ...prev,
          autoFilledFields,
        }));

        if (updateReportingParty) {
          updateReportingParty({ ...formData, autoFilledFields });
        }
      }
    }
  }, [profile, hasSavedData]);

  const effectiveValidationErrors =
    reportingPartyValidationErrors && reportingPartyValidationErrors.length > 0
      ? reportingPartyValidationErrors
      : validationErrors;
  const partyCategories = PARTY_CATEGORIES;
  const stateOptions = statesData.map((state) => ({
    name: state.abbreviation,
    value: state.abbreviation,
  }));

  const territoryOptions = territories.map((territory) => ({
    name: territory.name,
    value: territory.abbreviation,
  }));

  const canadaProvinceOptions = canadaProvinces.map((province) => ({
    name: province.abbreviation,
    value: province.abbreviation,
  }));

  const mexicoStateOptions = mexicoStates.map((state) => ({
    name: state.abbreviation,
    value: state.abbreviation,
  }));

  // Merge all countries for dropdown and remove duplicates
  const allCountryOptions = [
    ...territories.map((t) => ({ name: t.name, value: t.abbreviation })),
    ...foreignTerritories.map((t) => ({ name: t.name, value: t.abbreviation })),
  ]
    .filter((option, index, self) => index === self.findIndex((o) => o.value === option.value))
    .sort((a, b) => {
      const priorityOrder = [
        'United States',
        'American Samoa',
        'Guam',
        'Northern Mariana Islands',
        'Puerto Rico',
        'U.S. Virgin Islands',
        'Canada',
        'Mexico',
      ];
      const aIndex = priorityOrder.indexOf(a.name);
      const bIndex = priorityOrder.indexOf(b.name);
      if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;
      return a.name.localeCompare(b.name);
    });

  // Determine which state/province options to show based on country
  const getStateOptions = () => {
    const country = formData.usTerritory?.toUpperCase();
    if (country === 'US') {
      return stateOptions;
    } else if (isUSTerritory(country)) {
      return territoryOptions.filter((t) => t.value === country);
    } else if (country === 'CA') {
      return canadaProvinceOptions;
    } else if (country === 'MX') {
      return mexicoStateOptions;
    }
    return [];
  };

  const getStateLabel = () => {
    const country = formData.usTerritory?.toUpperCase();
    if (country === 'US' || isUSTerritory(country)) {
      return 'State/U.S. Territory';
    } else if (country === 'CA') {
      return 'Province/Territory';
    } else if (country === 'MX') {
      return 'State';
    }
    return 'State/Province';
  };

  const shouldRequireStateAndPostalCode = requiresStateAndPostalCode(formData.usTerritory);

  useEffect(() => {
    setFormSubmitted(submitted);
    if (submitted) {
      executeUpdateReportingPartyData();
      onFormSubmissionComplete && onFormSubmissionComplete();
    }
  }, [submitted]);

  const updateReportingPartyData = async (): Promise<FincenUpdateResponse> => {
    const token = await getAccessToken();
    return updateFincenReportingPartyAPI(String(fincenReport?.fincenReportId || 0), formData, token);
  };

  const {
    execute: executeUpdateReportingPartyData,
    value: updateReportingPartyResponse,
    status: updateReportingPartyStatus,
    errors: updateReportingPartyErrors,
  } = useAsync<FincenUpdateResponse>(updateReportingPartyData, false);

  useEffect(() => {
    if (updateReportingPartyStatus === AsyncStatus.SUCCESS) {
      addSnackbarMessage({
        message: 'Saving Successful',
        type: 'success',
      });
      fincenReportCtx?.reloadFincenReport?.(fileData?.fileId ?? '0');
      setFormSubmitted(false);
    }
  }, [updateReportingPartyStatus, addSnackbarMessage]);

  useEffect(() => {
    if (reportingPartyValidationErrors && reportingPartyValidationErrors.length > 0) {
      setFormSubmitted(true);
    }
  }, [reportingPartyValidationErrors]);

  const getFieldError = (fieldName: string): FieldValidationError | undefined => {
    const { autoValidationTriggered, generateButtonClicked } = fincenReportCtx ?? {};
    // Only show inline validation errors after Generate Report button is clicked
    if (!generateButtonClicked) return undefined;
    if (fieldName === 'partyType' && formData.partyType) return undefined;
    return effectiveValidationErrors?.find((err) => err.field === fieldName);
  };

  const handleFieldChange = (field: string, value: any) => {
    setFormData((prevData) => {
      const newState = { ...prevData, [field]: value };
      if (field === 'usTerritory') {
        // Reset postalCode when country changes
        newState.postalCode = '';
        // Auto-set state to territory value for US Territories, reset for other countries
        if (isUSTerritory(value?.toUpperCase())) {
          newState.state = value?.toUpperCase();
        } else {
          newState.state = '';
        }
      }

      if (field === 'firstName' || field === 'middleName' || field === 'lastName') {
        const originalValue = originalProfileValues[field as keyof typeof originalProfileValues] || '';
        const hasChanged = value !== originalValue;
        let updatedAutoFilledFields = [...(prevData.autoFilledFields || [])];

        if (hasChanged) {
          updatedAutoFilledFields = updatedAutoFilledFields.filter((f) => f !== field);
        } else if (!updatedAutoFilledFields.includes(field)) {
          updatedAutoFilledFields.push(field);
        }

        newState.autoFilledFields = updatedAutoFilledFields;
      }

      if (updateReportingParty) {
        updateReportingParty(newState);
      }
      return newState;
    });

    if (
      reportingPartyValidationErrors &&
      reportingPartyValidationErrors.length > 0 &&
      fincenReportCtx?.setReportingPartyValidationErrors
    ) {
      const updatedErrors = reportingPartyValidationErrors.filter((err) => err.field !== field);
      fincenReportCtx.setReportingPartyValidationErrors(updatedErrors);

      if (field === 'usTerritory') {
        const finalErrors = updatedErrors.filter((err) => err.field !== 'state');
        fincenReportCtx.setReportingPartyValidationErrors(finalErrors);
      }
    }

    if (onChange) {
      onChange(field, value);
    }
  };

  const handlePartyTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPartyType = e.target.value;
    handleFieldChange('partyType', newPartyType);
  };

  const handleClosingDateChange = (date: string | null) => {
    const dateValue = date || '';
    handleFieldChange('closingDate', dateValue);

    // Validate the date format
    if (date && date.trim() !== '') {
      // Check if the date string is complete (not partially filled like 02/__/____)
      const isPartialDate = date.includes('_');

      if (isPartialDate) {
        // If date is incomplete, add validation error
        if (fincenReportCtx?.setReportingPartyValidationErrors) {
          const newError: FieldValidationError = {
            field: 'closingDate',
            message: 'Invalid date',
          };
          const updatedErrors = [
            ...(reportingPartyValidationErrors || []).filter((err) => err.field !== 'closingDate'),
            newError,
          ];
          fincenReportCtx.setReportingPartyValidationErrors(updatedErrors);
        }
      } else {
        // Parse and validate the complete date
        const parsedDate = parse(date, 'MM/dd/yyyy', new Date());
        if (!isValid(parsedDate)) {
          // Add validation error for invalid date
          if (fincenReportCtx?.setReportingPartyValidationErrors) {
            const newError: FieldValidationError = {
              field: 'closingDate',
              message: 'Invalid date',
            };
            const updatedErrors = [
              ...(reportingPartyValidationErrors || []).filter((err) => err.field !== 'closingDate'),
              newError,
            ];
            fincenReportCtx.setReportingPartyValidationErrors(updatedErrors);
          }
        } else {
          // Check if the date components are within valid ranges
          const parts = date.split('/');
          if (parts.length === 3) {
            const month = parseInt(parts[0], 10);
            const day = parseInt(parts[1], 10);
            const year = parseInt(parts[2], 10);

            // Validate month (1-12), day (1-31), and year (reasonable range)
            if (month < 1 || month > 12 || day < 1 || day > 31 || year < 1900 || year > 2100) {
              if (fincenReportCtx?.setReportingPartyValidationErrors) {
                const newError: FieldValidationError = {
                  field: 'closingDate',
                  message: 'Invalid date',
                };
                const updatedErrors = [
                  ...(reportingPartyValidationErrors || []).filter((err) => err.field !== 'closingDate'),
                  newError,
                ];
                fincenReportCtx.setReportingPartyValidationErrors(updatedErrors);
              }
            } else {
              // Clear validation error if date is valid
              if (
                reportingPartyValidationErrors &&
                reportingPartyValidationErrors.length > 0 &&
                fincenReportCtx?.setReportingPartyValidationErrors
              ) {
                const updatedErrors = reportingPartyValidationErrors.filter((err) => err.field !== 'closingDate');
                fincenReportCtx.setReportingPartyValidationErrors(updatedErrors);
              }
            }
          } else {
            // Clear validation error if date is valid
            if (
              reportingPartyValidationErrors &&
              reportingPartyValidationErrors.length > 0 &&
              fincenReportCtx?.setReportingPartyValidationErrors
            ) {
              const updatedErrors = reportingPartyValidationErrors.filter((err) => err.field !== 'closingDate');
              fincenReportCtx.setReportingPartyValidationErrors(updatedErrors);
            }
          }
        }
      }
    } else {
      // Clear validation error if date is empty
      if (
        reportingPartyValidationErrors &&
        reportingPartyValidationErrors.length > 0 &&
        fincenReportCtx?.setReportingPartyValidationErrors
      ) {
        const updatedErrors = reportingPartyValidationErrors.filter((err) => err.field !== 'closingDate');
        fincenReportCtx.setReportingPartyValidationErrors(updatedErrors);
      }
    }
  };

  return (
    <div>
      <Box mt={3}>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <AgentNetDropdownSelector
              label="Reporting Party Category"
              required
              options={partyCategories}
              variant="outlined"
              fullWidth
              data-testid="fincen-reporting-party-category-select"
              defaultToFirst={false}
              showValidation={!!getFieldError('partyCategory')}
              value={formData.partyCategory}
              menuOption={(value) => handleFieldChange('partyCategory', value)}
            />
            {getFieldError('partyCategory') && (
              <Typography variant="caption" color="error">
                {getFieldError('partyCategory')?.message}
              </Typography>
            )}
          </Grid>
          <Grid item xs={12}>
            <Box mb={2}>
              <RadioGroup
                label="Reporting Party Type"
                options={[
                  {
                    label: 'Individual',
                    value: 'Individual',
                    dataTestId: 'fincen-reporting-party-type-individual-radio',
                  },
                  { label: 'Entity', value: 'Entity', dataTestId: 'fincen-reporting-party-type-entity-radio' },
                ]}
                onChange={handlePartyTypeChange}
                selected={{ value: formData.partyType }}
                row={true}
                compact={true}
                border={true}
                required={true}
                showValidation={formSubmitted || !!fincenReportCtx?.autoValidationTriggered}
                errs={effectiveValidationErrors}
                name="partyType"
                data-testid="fincen-reporting-party-type-radio"
              />
            </Box>
          </Grid>

          {formData.partyType === 'Individual' ? (
            <>
              <Grid item xs={3}>
                <AgentNetTextInput
                  label="First Name"
                  required
                  variant="outlined"
                  fullWidth
                  value={formData.firstName}
                  onChange={(e) => handleFieldChange('firstName', e.target.value)}
                  data-testid="fincen-reporting-party-firstname-input"
                  error={!!getFieldError('firstName')}
                  helperText={getFieldError('firstName')?.message}
                  endAdornment={renderMagicWandIcon('firstName')}
                />
              </Grid>

              <Grid item xs={3}>
                <AgentNetTextInput
                  label="Middle Name"
                  variant="outlined"
                  fullWidth
                  value={formData.middleName}
                  onChange={(e) => handleFieldChange('middleName', e.target.value)}
                  data-testid="fincen-reporting-party-middlename-input"
                  endAdornment={renderMagicWandIcon('middleName')}
                />
              </Grid>

              <Grid item xs={3}>
                <AgentNetTextInput
                  label="Last Name"
                  required
                  variant="outlined"
                  fullWidth
                  value={formData.lastName}
                  onChange={(e) => handleFieldChange('lastName', e.target.value)}
                  data-testid="fincen-reporting-party-lastname-input"
                  error={!!getFieldError('lastName')}
                  helperText={getFieldError('lastName')?.message}
                  endAdornment={renderMagicWandIcon('lastName')}
                />
              </Grid>

              <Grid item xs={3}>
                <AgentNetTextInput
                  label="Suffix"
                  variant="outlined"
                  fullWidth
                  value={formData.suffix}
                  onChange={(e) => handleFieldChange('suffix', e.target.value)}
                  data-testid="fincen-reporting-party-suffix-input"
                  error={!!getFieldError('suffix')}
                  helperText={getFieldError('suffix')?.message}
                />
              </Grid>
            </>
          ) : (
            <Grid item xs={6}>
              <AgentNetTextInput
                label="Entity Name"
                required
                variant="outlined"
                fullWidth
                value={formData.entityName}
                onChange={(e) => handleFieldChange('entityName', e.target.value)}
                data-testid="fincen-reporting-party-entity-name-input"
                error={!!getFieldError('entityName')}
                helperText={getFieldError('entityName')?.message}
              />
            </Grid>
          )}
        </Grid>
      </Box>
      <Box mt={2} mb={2}>
        <Typography variant="h3">Business Address</Typography>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={6}>
          <GoogleAutocompleteSearch
            label="Address 1"
            required
            variant="outlined"
            fullWidth
            value={formData.address1}
            onChange={(e) => handleFieldChange('address1', e.target.value)}
            data-testid="fincen-reporting-party-address1-input"
            name="address1"
            errs={effectiveValidationErrors}
            showValidation={!!getFieldError('address1')}
            stateConstraint={
              (!formData.usTerritory || formData.usTerritory === CountryCode.US) &&
              formData.state &&
              formData.state.trim() !== ''
                ? formData.state
                : undefined
            }
            onPlaceSelect={(place: any) => {
              const isUSCountry = !formData.usTerritory || formData.usTerritory === CountryCode.US;
              const next = {
                address1: place?.address1 ?? '',
                address2: place?.address2 ?? formData.address2 ?? '',
                city: place?.city ?? '',
                postalCode: place?.zipcode ?? '',
                state: isUSCountry ? place?.state ?? '' : formData.state,
              } as Partial<ReportingPartyFormData>;

              // Auto-set usTerritory to US first if not already set and state is available
              if (isUSCountry && !formData.usTerritory && next.state) {
                handleFieldChange('usTerritory', CountryCode.US);
              }

              handleFieldChange('address1', next.address1 || '');
              handleFieldChange('address2', next.address2 || '');
              handleFieldChange('city', next.city || '');
              handleFieldChange('postalCode', next.postalCode || '');
              if (isUSCountry) {
                handleFieldChange('state', next.state || '');
              }
            }}
          />
        </Grid>

        <Grid item xs={6}>
          <AgentNetTextInput
            label="Address 2"
            variant="outlined"
            fullWidth
            value={formData.address2}
            onChange={(e) => handleFieldChange('address2', e.target.value)}
            data-testid="fincen-reporting-party-address2-input"
          />
        </Grid>

        <Grid item xs={3}>
          <AgentNetTextInput
            label="City"
            required
            variant="outlined"
            fullWidth
            value={formData.city}
            onChange={(e) => handleFieldChange('city', e.target.value)}
            data-testid="fincen-reporting-party-city-input"
            error={!!getFieldError('city')}
            helperText={getFieldError('city')?.message}
          />
        </Grid>

        <Grid item xs={3}>
          <AgentNetDropdownSelector
            label="US/U.S. Territory"
            required
            options={allCountryOptions}
            variant="outlined"
            fullWidth
            value={formData.usTerritory}
            data-testid="fincen-reporting-party-country-select"
            showValidation={!!getFieldError('usTerritory')}
            defaultToFirst={false}
            menuOption={(value) => handleFieldChange('usTerritory', value)}
          />
          {getFieldError('usTerritory') && (
            <Typography variant="caption" color="error">
              {getFieldError('usTerritory')?.message}
            </Typography>
          )}
        </Grid>

        {shouldRequireStateAndPostalCode && (
          <Grid item xs={3}>
            <AgentNetDropdownSelector
              label={getStateLabel()}
              required
              options={getStateOptions()}
              variant="outlined"
              fullWidth
              value={formData.state}
              data-testid="fincen-reporting-party-state-select"
              showValidation={!!getFieldError('state')}
              error={!!getFieldError('state')}
              errs={getFieldError('state') ? [getFieldError('state')!] : undefined}
              defaultToFirst={false}
              menuOption={(value) => handleFieldChange('state', value)}
              disabled={isUSTerritory(formData.usTerritory)}
            />
            {getFieldError('state') && (
              <Typography variant="caption" color="error">
                {getFieldError('state')?.message}
              </Typography>
            )}
          </Grid>
        )}

        <Grid item xs={3}>
          {isUS(formData.usTerritory) || isUSTerritory(formData.usTerritory) ? (
            <USPostalCodeField
              label="Zip"
              required={shouldRequireStateAndPostalCode}
              variant="outlined"
              fullWidth
              value={formData.postalCode}
              onChange={(e) => handleFieldChange('postalCode', e.target.value)}
              data-testid="fincen-reporting-party-zip-input"
              error={!!getFieldError('postalCode')}
              helperText={getFieldError('postalCode')?.message}
            />
          ) : (
            <AgentNetTextInput
              label="Zip"
              required={shouldRequireStateAndPostalCode}
              variant="outlined"
              fullWidth
              value={formData.postalCode}
              onChange={(e) => handleFieldChange('postalCode', e.target.value)}
              data-testid="fincen-reporting-party-zip-input"
              error={!!getFieldError('postalCode')}
              helperText={getFieldError('postalCode')?.message}
            />
          )}
        </Grid>

        {/* Empty space after Zip when State is not shown to push Date of Closing to next row */}
        {!shouldRequireStateAndPostalCode && <Grid item xs={3} />}

        <Grid item xs={3}>
          <DateFieldString
            label="Date of Closing"
            required
            format="MM/dd/yyyy"
            value={formData.closingDate}
            onChange={handleClosingDateChange}
            fullWidth
            variant="outlined"
            qaAttribute="fincen-reporting-party-closing-date-input"
            placeholder="mm/dd/yyyy"
            error={!!getFieldError('closingDate')}
            helperText={getFieldError('closingDate')?.message}
            showValidation={formSubmitted || !!fincenReportCtx?.autoValidationTriggered}
            shouldPrioritizeOnChange={false}
          />
        </Grid>
      </Grid>
    </div>
  );
};

export default ReportingPartyForm;
